import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { providersService } from '../../shared/services/providers.service';
import type { PortfolioImage, ProviderStatus } from '../../shared/types';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { ConfirmDeleteByEmailDialog } from './ConfirmDeleteByEmailDialog';

const STATUS_COLOR: Record<ProviderStatus, 'default' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'default',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  SUSPENDED: 'error',
};

export function AdminProviderReviewPage() {
  const { id: providerId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { hasRole } = useAuth();
  const canDelete = hasRole('SUPER_ADMIN');

  const [rejectingOpen, setRejectingOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['admin', 'provider', providerId, 'detail'] as const,
    queryFn: () => providersService.adminGet(providerId!),
    enabled: !!providerId,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
    qc.invalidateQueries({ queryKey: ['admin', 'provider', providerId] });
  };

  const approve = useMutation({
    mutationFn: () => providersService.adminApprove(providerId!),
    onSuccess: () => {
      invalidateAll();
      notify.success(t('provider.statusApproved'));
    },
    onError: (err) => notify.error(translate(err)),
  });

  const reject = useMutation({
    mutationFn: () => providersService.adminReject(providerId!, rejectReason),
    onSuccess: () => {
      invalidateAll();
      notify.success(t('provider.statusRejected'));
      setRejectingOpen(false);
      setRejectReason('');
    },
    onError: (err) => notify.error(translate(err)),
  });

  // Deletion is gated by an emailed confirmation code — the dialog runs
  // the full request/confirm flow and calls onSuccess once the server has
  // soft-deleted the account.
  const onDeleteSuccess = () => {
    invalidateAll();
    qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    notify.success(t('adminUsers.deleted'));
    navigate('/admin/providers');
  };

  if (!providerId) return null;

  if (detail.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {translate(detail.error) ||
            t('admin.providerLoadError', {
              defaultValue: 'Provider introuvable.',
            })}
        </Alert>
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => navigate('/admin/providers')}
          sx={{ mt: 2 }}
        >
          {t('common.back', { defaultValue: 'Retour' })}
        </Button>
      </Container>
    );
  }

  // Defensive: backend should always return arrays, but in the rare case a
  // malformed response sneaks through we don't want the whole review page
  // to crash on `.map(…)` of undefined.
  const p = detail.data;
  const portfolio = p.portfolio ?? [];
  const services = p.services ?? [];
  const eventSpecs = p.eventSpecializations ?? [];
  const cover = portfolio[0]?.imageUrl;
  const location =
    p.cityName && p.countryName
      ? `${p.cityName}, ${p.countryName}`
      : p.city
        ? `${p.city.name}, ${p.city.country}`
        : '';
  const status = p.status as ProviderStatus;
  const canApprove = status !== 'APPROVED';
  const canReject = status !== 'REJECTED';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/admin/providers" underline="hover">
          {t('admin.providersTitle')}
        </Link>
        <Typography color="text.primary">{p.businessName}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        {cover && (
          <Box
            component="img"
            src={cover}
            alt={p.businessName}
            sx={{
              width: '100%',
              height: { xs: 140, sm: 200, md: 240 },
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={cover ?? undefined}
                sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}
              >
                {p.businessName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {p.businessName}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Chip
                    label={status}
                    color={STATUS_COLOR[status]}
                    size="small"
                  />
                  {location && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <LocationOnRoundedIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {location}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {status === 'APPROVED' && (
                <Button
                  variant="outlined"
                  endIcon={<OpenInNewRoundedIcon />}
                  onClick={() =>
                    window.open(`/providers/${p.id}`, '_blank', 'noopener')
                  }
                >
                  {t('admin.viewPublic', { defaultValue: 'Voir la page publique' })}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<PhotoLibraryRoundedIcon />}
                onClick={() => navigate(`/admin/providers/${p.id}/portfolio`)}
              >
                {t('admin.portfolioCta', { defaultValue: 'Portfolio' })}
              </Button>
              {canApprove && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleRoundedIcon />}
                  onClick={() => approve.mutate()}
                  disabled={approve.isPending}
                >
                  {t('admin.approve')}
                </Button>
              )}
              {canReject && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelRoundedIcon />}
                  onClick={() => setRejectingOpen(true)}
                >
                  {t('admin.reject')}
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteOpen(true)}
                >
                  {t('common.delete')}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Bio */}
        {p.bio && (
          <Grid item xs={12}>
            <Section title={t('provider.bio', { defaultValue: 'Présentation' })}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{p.bio}</Typography>
            </Section>
          </Grid>
        )}

        {/* Services */}
        <Grid item xs={12} md={6}>
          <Section
            title={t('provider.services', { defaultValue: 'Services' })}
            counter={services.length}
          >
            {services.length === 0 ? (
              <EmptyHint
                text={t('admin.noServices', {
                  defaultValue: 'Aucun service renseigné.',
                })}
              />
            ) : (
              <Stack divider={<Divider flexItem />} spacing={1.5}>
                {services.map((s) => (
                  <Box key={s.id}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {s.serviceName}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      {s.customPriceCents != null && (
                        <Chip
                          size="small"
                          label={new Intl.NumberFormat(undefined, {
                            style: 'currency',
                            currency: s.currency || 'EUR',
                          }).format(s.customPriceCents / 100)}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {s.durationMinutes && (
                        <Chip
                          size="small"
                          label={`${s.durationMinutes} min`}
                          variant="outlined"
                        />
                      )}
                      {s.categoryCode && (
                        <Chip
                          size="small"
                          label={s.categoryCode}
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    {s.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {s.description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Section>
        </Grid>

        {/* Event specializations */}
        <Grid item xs={12} md={6}>
          <Section
            title={t('provider.eventSpecializations', {
              defaultValue: 'Types d\'événements',
            })}
            counter={eventSpecs.length}
          >
            {eventSpecs.length === 0 ? (
              <EmptyHint
                text={t('admin.noEventSpecs', {
                  defaultValue: 'Aucun type d\'événement déclaré.',
                })}
              />
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {eventSpecs.map((e) => (
                  <Chip key={e.id} label={e.name} variant="outlined" />
                ))}
              </Stack>
            )}
          </Section>
        </Grid>

        {/* Portfolio */}
        <Grid item xs={12}>
          <Section
            title={t('provider.portfolio', { defaultValue: 'Portfolio' })}
            counter={portfolio.length}
            action={
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoLibraryRoundedIcon />}
                onClick={() => navigate(`/admin/providers/${p.id}/portfolio`)}
              >
                {t('admin.managePortfolio', { defaultValue: 'Gérer' })}
              </Button>
            }
          >
            {portfolio.length === 0 ? (
              <EmptyHint
                text={t('admin.noPortfolio', {
                  defaultValue: 'Aucune photo ajoutée.',
                })}
              />
            ) : (
              <Grid container spacing={1.5}>
                {portfolio.slice(0, 12).map((img) => (
                  <Grid item xs={4} sm={3} md={2} key={img.id}>
                    <PortfolioThumb image={img} businessName={p.businessName} />
                  </Grid>
                ))}
                {portfolio.length > 12 && (
                  <Grid item xs={4} sm={3} md={2}>
                    <Paper
                      variant="outlined"
                      sx={{
                        aspectRatio: '1 / 1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderStyle: 'dashed',
                      }}
                      onClick={() => navigate(`/admin/providers/${p.id}/portfolio`)}
                    >
                      <Typography variant="body2" color="text.secondary">
                        +{portfolio.length - 12}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </Section>
        </Grid>

        {/* Contact */}
        <Grid item xs={12} md={6}>
          <Section title={t('provider.contact', { defaultValue: 'Contact' })}>
            <Stack spacing={1}>
              <KV
                label={t('provider.whatsapp', { defaultValue: 'WhatsApp' })}
                value={p.whatsappNumber || '—'}
              />
              {p.social && (
                <>
                  <KV label="Instagram" value={p.social.instagram ?? '—'} link />
                  <KV label="Facebook" value={p.social.facebook ?? '—'} link />
                  <KV label="TikTok" value={p.social.tiktok ?? '—'} link />
                  <KV label="YouTube" value={p.social.youtube ?? '—'} link />
                  <KV label="Snapchat" value={p.social.snapchat ?? '—'} link />
                  <KV label="Twitter" value={p.social.twitter ?? '—'} link />
                </>
              )}
            </Stack>
          </Section>
        </Grid>

        {/* Meta */}
        <Grid item xs={12} md={6}>
          <Section title={t('admin.meta', { defaultValue: 'Métadonnées' })}>
            <Stack spacing={1}>
              <KV label="ID" value={p.id} />
              <KV
                label={t('common.createdAt', { defaultValue: 'Créé le' })}
                value={new Date(p.createdAt).toLocaleString()}
              />
              <KV label="Status" value={status} />
              <KV
                label={t('provider.rating', { defaultValue: 'Note moyenne' })}
                value={
                  p.ratingAvg != null && p.ratingCount
                    ? `${p.ratingAvg.toFixed(2)} (${p.ratingCount})`
                    : '—'
                }
              />
            </Stack>
          </Section>
        </Grid>
      </Grid>

      {/* Reject dialog */}
      <Dialog
        open={rejectingOpen}
        onClose={() => setRejectingOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('admin.reject')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('admin.rejectReason')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectingOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectReason || reject.isPending}
            onClick={() => reject.mutate()}
          >
            {t('admin.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteByEmailDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onSuccess={onDeleteSuccess}
        title={t('adminUsers.confirmTitle')}
        targetLabel={p.businessName}
        requestCode={() => providersService.adminRequestDelete(p.id)}
        confirm={(code) => providersService.adminDelete(p.id, code)}
      />
    </Container>
  );
}

function Section({
  title,
  counter,
  action,
  children,
}: {
  title: string;
  counter?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {counter != null && (
            <Chip size="small" label={counter} variant="outlined" />
          )}
        </Stack>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
      {text}
    </Typography>
  );
}

function KV({
  label,
  value,
  link = false,
}: {
  label: string;
  value: string;
  link?: boolean;
}) {
  const isUrl = link && value !== '—' && /^https?:\/\//.test(value);
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
        {label}
      </Typography>
      {isUrl ? (
        <Link
          href={value}
          target="_blank"
          rel="noopener"
          sx={{ wordBreak: 'break-all' }}
        >
          {value}
        </Link>
      ) : (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {value}
        </Typography>
      )}
    </Stack>
  );
}

function PortfolioThumb({
  image,
  businessName,
}: {
  image: PortfolioImage;
  businessName: string;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 1.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        component="img"
        src={image.imageUrl}
        alt={image.caption ?? businessName}
        loading="lazy"
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />
    </Box>
  );
}
